import colors from '../components/colors';
import {
    BannerInjectedAction,
    BootstrapFinishedAction,
    BootstrapStartedAction,
    BrowserCapabilitiesFetchedAction,
    CustomMetadatInjectedAction,
    HideBannerAction,
    HideCtaAction,
    LinkInjectedAction,
    LocalSettingsLoadedAction,
    LocationSettingsFetchedAction,
    SetCtaTextAction,
} from '../typedefs/actions';
import * as actionTypes from '../typedefs/actionTypes';
import { Settings } from '../typedefs/api';
import { Banner as BannerExternal, InlineConfig } from '../typedefs/external';
import { SettingsState as State } from '../typedefs/store';
import { createReducer } from './util';
import { isNumber, isString } from '../util';

const handlers = {
    [actionTypes.BOOTSTRAP_STARTED]: bootstrapStarted,
    [actionTypes.BOOTSTRAP_FINISHED]: bootstrapFinished,
    [actionTypes.BROWSER_CAPABILITIES_FETCHED]: browserCapabilitiesFetched,
    [actionTypes.LOCAL_SETTINGS_LOADED]: localSettingsLoaded,
    [actionTypes.LOCATION_SETTINGS_FETCHED]: locationSettingsFetched,
    [actionTypes.HIDE_BANNER]: hideBanner,
    [actionTypes.BANNER_INJECTED]: bannerInjected,
    [actionTypes.LINK_INJECTED]: linkInjected,
    [actionTypes.CUSTOM_METADATA_INJECTED]: customMetadataInjected,
    [actionTypes.HIDE_CTA]: hideCta,
    [actionTypes.SET_CTA_TEXT]: setCtaText,
};

function bootstrapStarted(state: State, action: BootstrapStartedAction): State {
    return { ...state, bootstrapped: false };
}

function bootstrapFinished(state: State, action: BootstrapFinishedAction): State {
    return { ...state, bootstrapped: true };
}

function localSettingsLoaded(state: State, action: LocalSettingsLoadedAction): State {
    return {
        ...state,
        hideBanner: !!action.payload.hideBanner,
    };
}

function locationSettingsFetched(state: State, action: LocationSettingsFetchedAction): State {
    const { settings, inlineConfig } = action.payload;
    let newState = {
        ...state,
        // default values
        ctaType: 'circle',
        messageListType: 'sidebar',
        // fields that we know about
        smsPhone: settings.smsPhone,
        locale: settings.settings.locale,
        banners: settings.banners,
        starters: settings.starters.primary,
        links: settings.links,
        requireVisitorFields: settings.settings.requireVisitorFields,
        requiredFields:
            settings.settings.requireVisitorFields && settings.settings.requiredFields
                ? settings.settings.requiredFields.split(',')
                : [],
        videoEnabled: settings.settings.videoEnabled,
        maxWaitSeconds: settings.settings.maxWaitSeconds,
        bypassCustomizePanel: settings.settings.bypassCustomizePanel,
        offlineOption: settings.settings.offlineAction,
        pusherKey: settings.pusherKey,
        settingsFetched: true,

        // dynamic fields
        ...extractCustomSettings(settings.settings),
    };

    return {
        ...newState,
        hideCta: inlineConfig.hideCta || false,
        customMetadata: inlineConfig.customMetadata,
        extraContext: inlineConfig.extraContext,
        openLinksExternally: newState.messageListType === 'cars.com',
        ...getCtaSettings(newState as State, inlineConfig), // settings for a particular ctaType
    } as State;
}

/**
 * Get settings specific to each CTA Type
 */
const getCtaSettings = (settings: State, inlineConfig: InlineConfig) => {
    const universalCtaSettings = {
        ctaBottomMargin: '0px',
        zIndex: '1220000',
    };

    if (settings.ctaBottomMargin) {
        const ctaBottomMargin = settings.ctaBottomMargin;
        if (
            isNumber(ctaBottomMargin) ||
            (isString(ctaBottomMargin) && /^\d+$/.test(ctaBottomMargin))
        ) {
            universalCtaSettings.ctaBottomMargin = `${ctaBottomMargin}px`;
        } else if (isString(ctaBottomMargin)) {
            universalCtaSettings.ctaBottomMargin = ctaBottomMargin;
        }
    }

    if (settings.zIndex) {
        const zIndex: any = settings.zIndex;
        if (isString(zIndex) && /^\d+$/.test(zIndex)) {
            universalCtaSettings.zIndex = zIndex;
        } else if (isNumber(settings.zIndex) && zIndex > 0) {
            universalCtaSettings.zIndex = settings.zIndex.toString();
        }
    }

    switch (settings.ctaType) {
        case 'tab':
            return {
                ...universalCtaSettings,
                ctaPosition: inlineConfig.ctaPosition
                    ? inlineConfig.ctaPosition
                    : settings.ctaPosition || 'right',
                ctaBackgroundColor: inlineConfig.ctaBackgroundColor
                    ? inlineConfig.ctaBackgroundColor
                    : settings.ctaBackgroundColor || colors.pink,
                ctaForegroundColor: inlineConfig.ctaForegroundColor
                    ? inlineConfig.ctaForegroundColor
                    : settings.ctaForegroundColor || colors.white,
                accentColor1: inlineConfig.accentColor1
                    ? inlineConfig.accentColor1
                    : settings.accentColor1 || colors.green,
                accentColor2: inlineConfig.accentColor2
                    ? inlineConfig.accentColor2
                    : settings.accentColor2 || colors.blue,
                ctaText: inlineConfig.ctaText
                    ? inlineConfig.ctaText
                    : settings.ctaText || 'Live Chat',
                ctaCustomImage: null,
            };
        case 'circle with tail':
            return {
                ...universalCtaSettings,
                ctaPosition: inlineConfig.ctaPosition
                    ? inlineConfig.ctaPosition
                    : settings.ctaPosition || 'bottom right',
                ctaBackgroundColor: inlineConfig.ctaBackgroundColor
                    ? inlineConfig.ctaBackgroundColor
                    : settings.ctaBackgroundColor || colors.blue,
                ctaForegroundColor: inlineConfig.ctaForegroundColor
                    ? inlineConfig.ctaForegroundColor
                    : settings.ctaForegroundColor || colors.white,
                ctaText: inlineConfig.ctaText
                    ? inlineConfig.ctaText
                    : settings.ctaText || 'Chat with us',
                ctaCustomImage: inlineConfig.ctaCustomImage
                    ? inlineConfig.ctaCustomImage
                    : settings.ctaCustomImage || null,
                ctaTailBackgroundColor: inlineConfig.ctaTailBackgroundColor
                    ? inlineConfig.ctaTailBackgroundColor
                    : settings.ctaTailBackgroundColor || colors.black,
                ctaTailForegroundColor: inlineConfig.ctaTailForegroundColor
                    ? inlineConfig.ctaTailForegroundColor
                    : settings.ctaTailForegroundColor || colors.white,
                accentColor1: inlineConfig.accentColor1
                    ? inlineConfig.accentColor1
                    : settings.accentColor1 || colors.green,
                accentColor2: inlineConfig.accentColor2
                    ? inlineConfig.accentColor2
                    : settings.accentColor2 || colors.blue,
            };
        case 'custom':
            return {
                ...universalCtaSettings,
                ctaPosition: null,
                ctaBackgroundColor: null,
                ctaForegroundColor: null,
                ctaText: null,
                ctaCustomImage: null,
            };
        case 'circle':
            return {
                ...universalCtaSettings,
                ctaPosition: inlineConfig.ctaPosition
                    ? inlineConfig.ctaPosition
                    : settings.ctaPosition || 'bottom right',
                ctaBackgroundColor: inlineConfig.ctaBackgroundColor
                    ? inlineConfig.ctaBackgroundColor
                    : settings.ctaBackgroundColor || colors.blue,
                ctaForegroundColor: inlineConfig.ctaForegroundColor
                    ? inlineConfig.ctaForegroundColor
                    : settings.ctaForegroundColor || colors.white,
                accentColor1: inlineConfig.accentColor1
                    ? inlineConfig.accentColor1
                    : settings.accentColor1 || colors.green,
                accentColor2: inlineConfig.accentColor2
                    ? inlineConfig.accentColor2
                    : settings.accentColor2 || colors.blue,
                ctaText: null,
                ctaCustomImage: inlineConfig.ctaCustomImage
                    ? inlineConfig.ctaCustomImage
                    : settings.ctaCustomImage || null,
            };
        default:
            throw new Error(`Unsupported ctaType "${settings.ctaType}".`);
    }
};

/**
 * Location settings are dynamically generated from a key/value store, so we don't know
 * what to expect. This function extracts keys that match embed.* and normalizes them. For
 * example:
 *
 *  {
 *      embed.ctaText: 'Chat Now',
 *      other.managedChat: false,
 *  }
 *
 *  is normalized to
 *
 *  {
 *      ctaText: 'Chat Now',
 *  }
 */
function extractCustomSettings(settings: Settings) {
    const regex = /^embed\.(.*)$/i;
    return Object.keys(settings).reduce((keys, key) => {
        const matches = key.match(regex);
        if (matches) {
            const setting = matches[1];
            keys[setting] = settings[key];
        }
        return keys;
    }, {});
}

function hideBanner(state: State, action: HideBannerAction): State {
    return { ...state, hideBanner: true };
}

function browserCapabilitiesFetched(state: State, action: BrowserCapabilitiesFetchedAction): State {
    return { ...state, browser: action.payload };
}

function bannerInjected(state: State, action: BannerInjectedAction): State {
    return {
        ...state,
        banners: action.payload.banners.map((banner: BannerExternal) => {
            return { banner: banner.text, url: banner.url };
        }),
    };
}
function linkInjected(state: State, action: LinkInjectedAction): State {
    return {
        ...state,
        links: action.payload.links,
    };
}

function customMetadataInjected(state: State, action: CustomMetadatInjectedAction): State {
    return {
        ...state,
        customMetadata: action.payload.customMetadata,
    };
}

function hideCta(state: State, action: HideCtaAction): State {
    return { ...state, hideCta: true };
}

function setCtaText(state: State, action: SetCtaTextAction): State {
    return { ...state, ctaText: action.payload.ctaText };
}

export default createReducer({}, handlers);



// WEBPACK FOOTER //
// ./~/tslint-loader!./src/reducers/settings.ts